import { useState, useEffect } from 'react';
import { Upload, Check, X, Image as ImageIcon, Trash2 } from 'lucide-react';
import { uploadAlbumArt, listAlbumArt, sanitizeFilename, deleteAlbumArt, getAlbumArtUrl } from '../lib/storageUtils';
import { albumArtMapping } from '../lib/albumArtMapping';
import { MOCK_CARDS } from '../lib/mockData';
import Header from '../components/Header';
import { useApp } from '../lib/AppContext';
import { useAuth } from '../lib/AuthContext';
import { isAdmin } from '../lib/adminUtils';

export default function UploadAlbumArt() {
  const { navigateTo, refreshSongs } = useApp();
  const { user } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [selectedCard, setSelectedCard] = useState<string>('');
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<string[]>([]);
  const [deleting, setDeleting] = useState<string | null>(null);

  useEffect(() => {
    if (!user || !isAdmin(user.email)) {
      navigateTo('settings');
      return;
    }

    const loadUploadedFiles = async () => {
      const { files } = await listAlbumArt();
      setUploadedFiles(files);
    };

    loadUploadedFiles();
  }, [user, navigateTo]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setMessage({ type: 'error', text: 'Please select an image file' });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'File must be less than 5MB' });
      return;
    }

    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    setMessage(null);
  };

  const loadUploadedFiles = async () => {
    const { files } = await listAlbumArt();
    setUploadedFiles(files);
  };

  const handleDelete = async (filename: string) => {
    if (!confirm(`Delete ${filename}?`)) return;

    setDeleting(filename);
    setMessage(null);

    const result = await deleteAlbumArt(filename);

    setDeleting(null);

    if (result.error) {
      setMessage({ type: 'error', text: result.error });
    } else {
      setMessage({ type: 'success', text: `Deleted ${filename}` });
      loadUploadedFiles();
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !selectedCard) {
      setMessage({ type: 'error', text: 'Please select both a card and an image' });
      return;
    }

    setUploading(true);
    setMessage(null);

    const filename = albumArtMapping[selectedCard];
    const result = await uploadAlbumArt(selectedFile, filename);

    setUploading(false);

    if ('error' in result) {
      setMessage({ type: 'error', text: result.error });
    } else {
      setMessage({ type: 'success', text: `Uploaded successfully as ${filename}` });
      setSelectedFile(null);
      setPreviewUrl('');
      setSelectedCard('');
      loadUploadedFiles();
      // Refresh songs to reload album art with cache-busting
      await refreshSongs();
    }
  };

  const card = MOCK_CARDS.find(c => c.id === selectedCard);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#111111] via-[#1a1a1a] to-[#111111]">
      <Header
        mode="title"
        title="Upload Album Art"
        showBackButton
        onBackClick={() => navigateTo('settings')}
      />

      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-6">
          <p className="text-gray-400">
            Add images to Supabase Storage for songs in the catalog
          </p>
        </div>

        {message && (
          <div className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
            message.type === 'success'
              ? 'bg-green-500/10 border border-green-500/20 text-green-400'
              : 'bg-red-500/10 border border-red-500/20 text-red-400'
          }`}>
            {message.type === 'success' ? <Check size={20} /> : <X size={20} />}
            <span>{message.text}</span>
          </div>
        )}

        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Select Song
            </label>
            <select
              value={selectedCard}
              onChange={(e) => setSelectedCard(e.target.value)}
              className="w-full bg-[#222] border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#40E0D0]"
            >
              <option value="">Choose a song...</option>
              {MOCK_CARDS.map(card => (
                <option key={card.id} value={card.id}>
                  {card.artistName} - {card.songTitle}
                </option>
              ))}
            </select>
            {card && (
              <p className="mt-2 text-sm text-gray-400">
                Will be saved as: <span className="text-[#40E0D0] font-mono">{albumArtMapping[card.id]}</span>
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Select Image File
            </label>
            <div className="relative">
              <input
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp"
                onChange={handleFileSelect}
                className="hidden"
                id="file-upload"
              />
              <label
                htmlFor="file-upload"
                className="flex items-center justify-center gap-3 w-full bg-[#222] border-2 border-dashed border-gray-700 rounded-lg px-4 py-8 cursor-pointer hover:border-[#40E0D0] transition-colors"
              >
                <Upload size={24} className="text-gray-400" />
                <span className="text-gray-400">
                  {selectedFile ? selectedFile.name : 'Click to select an image'}
                </span>
              </label>
            </div>
            <p className="mt-2 text-xs text-gray-500">
              Supported formats: JPEG, PNG, WebP (max 5MB)
            </p>
          </div>

          {previewUrl && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Preview
              </label>
              <div className="bg-[#222] rounded-lg p-4 flex items-center justify-center">
                <img
                  src={previewUrl}
                  alt="Preview"
                  className="max-w-xs max-h-64 rounded-lg object-cover"
                />
              </div>
            </div>
          )}

          <button
            onClick={handleUpload}
            disabled={uploading || !selectedFile || !selectedCard}
            className="w-full bg-[#40E0D0] text-black font-semibold py-3 rounded-lg hover:bg-[#47EAD8] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {uploading ? (
              <>
                <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload size={20} />
                Upload to Storage
              </>
            )}
          </button>
        </div>

        <div className="mt-12">
          <h2 className="text-xl font-bold text-white mb-4">Uploaded Files ({uploadedFiles.length})</h2>
          {uploadedFiles.length === 0 ? (
            <div className="bg-[#1a1a1a] rounded-lg p-8 text-center">
              <ImageIcon size={48} className="mx-auto text-gray-600 mb-3" />
              <p className="text-gray-400">No images uploaded yet</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {uploadedFiles.map(filename => (
                <div key={filename} className="bg-[#1a1a1a] rounded-lg overflow-hidden group relative">
                  <div className="aspect-square bg-[#222] flex items-center justify-center overflow-hidden">
                    <img
                      src={getAlbumArtUrl(filename, true)}
                      alt={filename}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                        (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden');
                      }}
                    />
                    <ImageIcon size={32} className="text-gray-600 hidden" />
                  </div>
                  <div className="p-3 flex items-center justify-between gap-2">
                    <p className="text-xs text-gray-400 truncate flex-1" title={filename}>
                      {filename}
                    </p>
                    <button
                      onClick={() => handleDelete(filename)}
                      disabled={deleting === filename}
                      className="flex-shrink-0 p-1.5 bg-red-500/10 hover:bg-red-500/20 text-red-500 rounded transition-colors disabled:opacity-50"
                      title="Delete"
                    >
                      {deleting === filename ? (
                        <div className="w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Trash2 size={16} />
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
